import {
  Checkbox,
  createStyles,
  makeStyles,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  TableBody,
  CircularProgress,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React from 'react';
import { PaymentConnection, PaymentOrderField, Payment, OrderDirection, Maybe } from '../gql-types.generated';
import TableHeader, { HeaderCell } from './TableHeader';
import InfiniteScroller from 'react-infinite-scroller';
import { paymenteStatusDisplay } from '../util/PaymentStatus';

// Column titles for mapping and sorting
const paymentHeaderCells: HeaderCell[] = [
  { id: 'status', sortable: false, label: 'Status' },
  { id: 'paymentId', sortable: false, label: 'Payment ID' },
  { id: 'captureType', sortable: false, label: 'Capture Type' },
  { id: 'amount', sortable: false, label: 'Amount', align: 'left' },
  { id: 'date', sortable: false, label: 'Last Updated', direction: OrderDirection.Desc, field: PaymentOrderField.Timestamp },
  { id: 'actions', sortable: false, label: '' },
];

const useStyles = makeStyles(() =>
  createStyles({
    table: {
      minWidth: 650,
      overflow: 'auto',
      tableLayout: 'fixed',
    },
    tableWrap: {
      height: 'calc(100vh - 210px)',
      minHeight: 'calc(100vh - 210px)', // or 920 to align with the 3rd box
      '&::before': {
        content: '""',
        display: 'block',
        position: 'sticky',
        height: '6px',
        top: '0.5px',
        backgroundColor: '#F5F5F5',
        boxShadow: '0px 50px 6px #747579',
      },
    },
    actionsCell: {
      padding: 8,
      minWidth: 145,
    },
    tableRow: {
      '&$selected, &$selected:hover': {
        backgroundColor: '#EEF4FE',
      },
    },
    selected: {},
    progressBox: {
      height: 60,
      width: 60,
      display: 'inline-block',
    },
    tablecell: {
      padding: 8,
      fontSize: 16,
      wordWrap: 'break-word',
    },
  }),
);

// Table for the payments
interface PaymentListProps {
  defaultCurrency?: string | null;
  paymentConnection: PaymentConnection;
  payments: Payment[] | undefined;
  direction: OrderDirection;
  field: string;
  handleSort: (arrowDirection: OrderDirection, fieldName: string) => void;
  loadPage: (endEdge: string) => void;
  selectedRecord?: Payment;
  setSelectedRecord: (record: Payment | undefined) => void;
  handleViewPayment: (record: Payment | undefined) => void;
  handleRefundPayment: (record: Payment | undefined) => void;
  isLoadingPayments: boolean;
}

const PaymentList: React.FC<PaymentListProps> = props => {
  const classes = useStyles();
  const {
    defaultCurrency,
    paymentConnection,
    payments,
    direction,
    field,
    handleSort,
    handleViewPayment,
    loadPage,
    selectedRecord,
    setSelectedRecord,
    isLoadingPayments,
  } = props;

  // Payment object fields
  const hasMoreData = paymentConnection?.pageInfo.hasNextPage as boolean;

  // Handling row selection
  const handleRowClick = (event: unknown, row: Payment) => {
    if (row) {
      // Select record / or unselect if already selected
      setSelectedRecord(row.id === selectedRecord?.id ? undefined : row);
    }
  };

  const handlePageLoad = (page: number) => {
    console.log(`loading page: ${page}`);
    if (!paymentConnection) {
      // There is not connection to use for retrieving the next page
      return;
    }

    // No more pages to load, no need to send the load request
    if (!paymentConnection.pageInfo.hasNextPage) {
      return;
    }

    if (!paymentConnection.pageInfo.endCursor) {
      return;
    }

    loadPage(paymentConnection.pageInfo.endCursor);
  };

  const skeletonDesktop: JSX.Element[] = [];
  for (let i = 0; i < 10; i += 1) {
    skeletonDesktop.push(
      <TableRow key={i}>
        <TableCell style={{ paddingLeft: '60px' }} className={classes.tablecell}>
          <Checkbox disabled />
        </TableCell>
        <TableCell className={classes.tablecell}>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell className={classes.tablecell}>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell className={classes.tablecell}>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell align="left" className={classes.tablecell}>
          <Skeleton height="1.5em" width="40%" />
        </TableCell>
        <TableCell className={classes.tablecell}>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell />
      </TableRow>,
    );
  }

  return (
    <TableContainer className={classes.tableWrap}>
      <InfiniteScroller
        pageStart={0}
        loadMore={handlePageLoad}
        hasMore={hasMoreData}
        loader={
          <div className={classes.progressBox}>
            <CircularProgress key={0} size={42} />
          </div>
        }
        initialLoad={false}
        useWindow={false}
      >
        <Table className={classes.table} aria-label="Payments Table" stickyHeader>
          <col width="15%" />
          <col width="15%" />
          <col width="17%" />
          <col width="15%" />
          <col width="18%" />
          <col width="15%" />
          <col width="5%" />
          <TableHeader order={direction} orderBy={field} handleSort={handleSort} cells={paymentHeaderCells} />
          <TableBody data-cy="payments-table-body">
            {isLoadingPayments ? (
              <>{skeletonDesktop}</>
            ) : (
              payments?.map((row: Maybe<Payment>) => {
                const node = row;
                if (!node) {
                  return (
                    <TableRow>
                      <TableCell colSpan={paymentHeaderCells.length}></TableCell>
                    </TableRow>
                  );
                }
                const isSelected = node.id === selectedRecord?.id;
                return (
                  <TableRow
                    className={classes.tableRow}
                    classes={{ selected: classes.selected }}
                    key={node.id}
                    hover
                    selected={isSelected}
                    onClick={event => {
                      handleRowClick(event, node);
                    }}
                    onDoubleClick={() => handleViewPayment(node)}
                  >
                    <TableCell style={{ paddingLeft: '60px' }} className={classes.tablecell}>
                      <Checkbox checked={isSelected} color="primary" />
                    </TableCell>
                    <TableCell className={classes.tablecell}>
                      {
                        paymenteStatusDisplay.find(item => {
                          return item.name === node.status;
                        })?.display
                      }
                    </TableCell>
                    <TableCell className={classes.tablecell}>***{node.id.slice(-9)}</TableCell>
                    <TableCell className={classes.tablecell}>{node.immediateCapture ? 'Immediate' : 'Manual'}</TableCell>
                    {/* <TableCell>{row.name}</TableCell> */}
                    <TableCell align="left" className={classes.tablecell}>
                      {/* TODO: Shouldn't there be a currency on the payment? */}
                      {node.amount && typeof node.amount === 'number'
                        ? new Intl.NumberFormat('en', {
                            style: 'currency',
                            currency: defaultCurrency || 'USD',
                            currencyDisplay: 'symbol',
                          }).format(node.amount / 100)
                        : '- -'}
                    </TableCell>
                    <TableCell className={classes.tablecell}>
                      {(node.completedTimestamp && new Date(node.completedTimestamp).toLocaleDateString()) ||
                        (node.createdAt && new Date(node.createdAt).toLocaleDateString()) ||
                        '- -'}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </InfiniteScroller>
    </TableContainer>
  );
};

export default PaymentList;
